// Notifications service
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import logger from '../utils/logger';
import { API_BASE } from '../config/api';
import { httpRequest } from '../utils/httpClient';
import { authService } from './auth';

/**
 * Subscribe to new notifications via backend polling.
 *
 * Replaces the previous Supabase Realtime subscription, which relied on
 * direct anon-key access to the notifications table. With RLS enabled
 * (backend-only access), the frontend polls the authenticated backend
 * endpoint instead.
 *
 * @param {string} userId - Current user's ID (kept for interface compatibility)
 * @param {function} callback - Callback function for new notifications
 * @returns {function} Unsubscribe function
 */
export function subscribeToNotifications(userId, callback) {
  if (!userId) return () => {};

  const POLL_INTERVAL_MS = 15000;
  const seenIds = new Set();
  let active = true;
  let seeded = false;

  const poll = async () => {
    if (!active) return;
    try {
      const res = await httpRequest(`${API_BASE}/notifications`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (!res.ok) return;
      const items = await res.json();
      if (!Array.isArray(items)) return;

      for (const item of items) {
        if (item && item.id && !seenIds.has(item.id)) {
          seenIds.add(item.id);
          // Only invoke callback for notifications discovered after the initial seed
          if (seeded) {
            callback(item);
          }
        }
      }
      seeded = true;
    } catch (err) {
      logger.debug('Notification poll failed:', err);
    }
  };

  // Seed seen IDs with current notifications, then poll for new ones
  poll();
  const intervalId = setInterval(poll, POLL_INTERVAL_MS);

  return () => {
    active = false;
    clearInterval(intervalId);
  };
}

/**
 * Initialize mobile notification permissions and setup
 */
export async function initializeMobileNotifications() {
  if (!Capacitor.isNativePlatform()) {
    // For web, initialize browser notifications
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        logger.debug('Web notification permission:', permission);
        return { granted: permission === 'granted', setup: permission === 'granted' };
      }
      return { granted: Notification.permission === 'granted', setup: true };
    }
    return { granted: false, setup: false };
  }

  try {
    // Request permission for local notifications only
    const localPermission = await LocalNotifications.requestPermissions();
    const granted = localPermission.display === 'granted';

    if (granted) {
      logger.debug('Local notifications permission granted');
    }

    return { granted, setup: granted };
  } catch (error) {
    logger.error('Error initializing mobile notifications:', error);
    return { granted: false, setup: false, error };
  }
}

/**
 * Show a local notification on mobile devices
 */
export async function showLocalNotification({ title, body, id, data = {} }) {
  if (!Capacitor.isNativePlatform()) {
    // For web, request permission first if needed
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          logger.debug('Notification permission denied');
          return;
        }
      }

      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `pista-${id}`,
          requireInteraction: false,
          data
        });
      }
    }
    return;
  }

  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: id || Date.now(),
          title,
          body,
          data,
          sound: 'default',
          smallIcon: 'ic_stat_notification',
          largeIcon: 'ic_launcher',
          iconColor: '#1976d2'
        }
      ]
    });
  } catch (error) {
    logger.error('Error showing local notification:', error);
  }
}

/**
 * Update app badge with notification count
 */
export async function updateAppBadge(count) {
  if (!Capacitor.isNativePlatform()) {
    // For web, update favicon badge or document title
    if (count > 0) {
      document.title = `(${count}) Pista - Your board game companion`;
    } else {
      document.title = 'Pista - Your boardgame companion';
    }
    return;
  }

  try {
    // Use LocalNotifications to set badge (if supported)
    // Note: Badge support depends on platform and may need additional configuration
    if (count > 0) {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: 999999, // Special ID for badge
            title: '',
            body: '',
            data: { badge: count },
            schedule: { at: new Date(Date.now() + 1000) } // Schedule 1 second in future
          }
        ]
      });
    }
  } catch (error) {
    logger.error('Error updating app badge:', error);
  }
}

/**
 * Get push notification token
 */
export async function getPushToken() {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  try {
    const { value } = await Preferences.get({ key: 'pushToken' });
    return value;
  } catch (error) {
    logger.error('Error getting push token:', error);
    return null;
  }
}

/**
 * Show notification for new game session or reminder
 */
export async function showGameNotification(notification) {
  const title = notification.title || 'Pista Notification';
  const body = notification.message || notification.body || 'You have a new notification';

  await showLocalNotification({
    title,
    body,
    id: notification.id || Date.now(),
    data: {
      type: notification.type || 'general',
      game_id: notification.game_id || null,
      notification_id: notification.id
    }
  });
}
